import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useAnnouncements } from "@/services/announcements-service";
import { AnnouncementWithReadStatus } from "@/types/announcements";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Bell, ChevronLeft, ChevronRight, ExternalLink, Link2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import appSound from '@/services/app-sound-service';

// Create context for the announcement banner state
interface AnnouncementBannerContextType {
  currentAnnouncement: AnnouncementWithReadStatus | null;
  currentAnnouncements: AnnouncementWithReadStatus[];
  currentIndex: number;
  loading: boolean;
  showBanner: boolean;
  handleNext: () => void;
  handlePrevious: () => void;
  handleDismiss: (temporary?: boolean) => Promise<void>;
  handleClose: () => void;
  loadAnnouncements: () => Promise<void>;
  hasMultipleAnnouncements: boolean;
}

const AnnouncementBannerContext = createContext<AnnouncementBannerContextType | null>(null);

// Provider component for the announcement banner state
export function AnnouncementBannerProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const announcements = useAnnouncements();
  const [currentAnnouncements, setCurrentAnnouncements] = useState<AnnouncementWithReadStatus[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  
  // Define markAsRead function outside useEffect to avoid conditional hook calling
  const markAsRead = useCallback(async (announcement: AnnouncementWithReadStatus) => {
    if (!user || !announcement || announcement.readStatus?.read) return;
    
    try {
      // Mark locally first to avoid repeated API calls if we get errors
      setCurrentAnnouncements(prevAnnouncements => {
        const newAnnouncements = [...prevAnnouncements];
        const index = newAnnouncements.findIndex(a => a.id === announcement.id);
        if (index >= 0) {
          newAnnouncements[index] = {
            ...newAnnouncements[index],
            readStatus: {
              ...(newAnnouncements[index].readStatus || { 
                announcementId: announcement.id,
                dismissCount: 0,
                permanentlyDismissed: false
              }),
              read: true
            }
          };
        }
        return newAnnouncements;
      });
      
      // Then try to mark it on the server, but don't block UI on this
      announcements.markAsRead(announcement.id).catch(error => {
        console.warn("Failed to mark announcement as read on server, but UI is updated:", error);
      });
    } catch (error) {
      console.error("Failed to mark announcement as read locally:", error);
    }
  }, [user, announcements]);
  
  // Load announcements function that can be called when navigating to games page
  const loadAnnouncements = useCallback(async () => {
    // Skip if no user
    if (!user) {
      setLoading(false);
      console.log("[AnnouncementProvider] No user, skipping announcement loading");
      return;
    }
    
    try {
      console.log("[AnnouncementProvider] Started loading announcements");
      setLoading(true);
      const userAnnouncements = await announcements.getAnnouncementsForCurrentUser();
      
      console.log("[AnnouncementProvider] Received announcements from API:", userAnnouncements);
      
      // Filter announcements based on various criteria
      const filteredAnnouncements = userAnnouncements.filter(announcement => {
        // Skip announcements that have reached max dismiss count
        const maxDismissCount = (announcement as any).maxDismissCount || 0;
        const currentDismissCount = announcement.dismissCount || 0;
        
        if (maxDismissCount > 0 && currentDismissCount >= maxDismissCount) {
          console.log(`[AnnouncementProvider] Announcement ${announcement.id} has reached max dismiss count (${currentDismissCount}/${maxDismissCount})`);
          return false;
        }
        
        // Check if announcement was recently dismissed in this browser session
        const localDismissKey = `announcement-temp-dismiss-${announcement.id}-${user.id}`;
        const tempDismissUntil = localStorage.getItem(localDismissKey);
        
        if (tempDismissUntil) {
          const dismissedUntil = new Date(tempDismissUntil);
          if (dismissedUntil > new Date()) {
            // Still within dismissal period, don't show
            console.log(`[AnnouncementProvider] Announcement ${announcement.id} is temporarily dismissed until ${dismissedUntil}`);
            return false;
          } else {
            // Dismissal period expired, remove from localStorage
            localStorage.removeItem(localDismissKey);
          }
        }
        
        return true;
      });
      
      // Update state
      setCurrentAnnouncements(filteredAnnouncements);
      
      // Show banner if there are announcements that are visible after filtering
      if (filteredAnnouncements.length > 0 && filteredAnnouncements.some(a => a.isVisible)) {
        console.log("[AnnouncementProvider] Found visible announcements, showing banner");
        setShowBanner(true);
      } else {
        console.log("[AnnouncementProvider] No visible announcements found, hiding banner");
        setShowBanner(false);
      }
    } catch (error) {
      console.error("[AnnouncementProvider] Failed to load announcements:", error);
      setShowBanner(false);
    } finally {
      setLoading(false);
    }
  }, [user, announcements]);
  
  // Mark current announcement as read when it's viewed
  useEffect(() => {
    if (!currentAnnouncements.length || !showBanner) return;
    
    const currentAnnouncement = currentAnnouncements[currentIndex];
    if (currentAnnouncement) {
      markAsRead(currentAnnouncement);
    }
  }, [currentIndex, currentAnnouncements, showBanner, markAsRead]);

  // Handle navigation between multiple announcements
  const handleNext = useCallback(() => {
    // Play button sound when navigating
    appSound.playButtonSound();
    setCurrentIndex((prev) => (prev + 1) % currentAnnouncements.length);
  }, [currentAnnouncements.length]);

  const handlePrevious = useCallback(() => {
    // Play button sound when navigating
    appSound.playButtonSound();
    setCurrentIndex((prev) => (prev - 1 + currentAnnouncements.length) % currentAnnouncements.length);
  }, [currentAnnouncements.length]);

  // Handle dismissal
  const handleDismiss = useCallback(async (permanent: boolean = false) => {
    if (!user || currentAnnouncements.length === 0) return;
    
    const currentAnnouncement = currentAnnouncements[currentIndex];
    if (!currentAnnouncement) return;

    try {
      if (permanent) {
        await announcements.dismissPermanently(currentAnnouncement.id);
      } else {
        // Store a temporary dismissal in localStorage only after successful API call
        const result = await announcements.dismissTemporarily(currentAnnouncement.id, 12);
        
        if (result && result.success) {
          const localDismissKey = `announcement-temp-dismiss-${currentAnnouncement.id}-${user.id}`;
          const dismissUntil = new Date();
          dismissUntil.setHours(dismissUntil.getHours() + 12);
          localStorage.setItem(localDismissKey, dismissUntil.toISOString());
        }
      }

      // Remove the dismissed announcement from the list
      setCurrentAnnouncements((prev) => {
        const newAnnouncements = [...prev];
        newAnnouncements.splice(currentIndex, 1);
        
        // Adjust currentIndex if needed
        if (currentIndex >= newAnnouncements.length && newAnnouncements.length > 0) {
          setCurrentIndex(newAnnouncements.length - 1);
        }
        
        // Hide banner if no more announcements
        if (newAnnouncements.length === 0) {
          setShowBanner(false);
        }
        
        return newAnnouncements;
      });

      // Play a sound when closing, if applicable
      appSound.playButtonSound();
    } catch (error) {
      console.error("Failed to dismiss announcement:", error);
      // Show error toast to user
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to dismiss announcement",
        variant: "destructive"
      });
    }
  }, [user, announcements, currentAnnouncements, currentIndex, appSound]);

  const handleClose = useCallback(() => {
    // Play a sound when closing, if applicable
    appSound.playButtonSound();
    
    if (currentAnnouncements.length > 0) {
      const currentAnnouncement = currentAnnouncements[currentIndex];
      
      // Store a temporary dismissal in localStorage when user closes the banner
      // This prevents the banner from showing again on refresh for some time
      if (user && currentAnnouncement) {
        const localDismissKey = `announcement-temp-dismiss-${currentAnnouncement.id}-${user.id}`;
        const dismissUntil = new Date();
        dismissUntil.setHours(dismissUntil.getHours() + 12); // Don't show again for 12 hours
        localStorage.setItem(localDismissKey, dismissUntil.toISOString());
        
        // Increment dismiss counter in the database
        // This helps track against maxDismissCount set by manager
        announcements.dismissTemporarily(currentAnnouncement.id, 12)
          .catch(error => {
            console.warn("Failed to increment dismiss count:", error);
            // Continue showing the announcement normally on next load if this fails
          });
      }
    }
    
    // Close the dialog
    setShowBanner(false);
  }, [currentAnnouncements, currentIndex, user, announcements]);

  const value = {
    currentAnnouncement: currentAnnouncements.length > 0 ? currentAnnouncements[currentIndex] : null,
    currentAnnouncements,
    currentIndex,
    loading,
    showBanner,
    handleNext,
    handlePrevious,
    handleDismiss,
    handleClose,
    loadAnnouncements,
    hasMultipleAnnouncements: currentAnnouncements.length > 1
  };
  
  return (
    <AnnouncementBannerContext.Provider value={value}>
      {children}
    </AnnouncementBannerContext.Provider>
  );
}

// Hook for using the announcement banner state
export function useAnnouncementBanner() {
  const context = useContext(AnnouncementBannerContext);
  if (!context) {
    throw new Error('useAnnouncementBanner must be used within an AnnouncementBannerProvider');
  }
  return context;
}

// Helper function to get the appropriate icon and color based on announcement type
const getTypeStyles = (type: 'info' | 'warning' | 'error' | 'success' | 'promo') => {
  switch(type) {
    case 'info':
      return {
        color: 'text-blue-500',
        iconBg: 'bg-blue-500/20',
        border: 'border-blue-500/30',
        bg: 'bg-blue-950/20'
      };
    case 'warning':
      return {
        color: 'text-amber-500',
        iconBg: 'bg-amber-500/20',
        border: 'border-amber-500/30',
        bg: 'bg-amber-950/20'
      };
    case 'error':
      return {
        color: 'text-red-500',
        iconBg: 'bg-red-500/20',
        border: 'border-red-500/30',
        bg: 'bg-red-950/20'
      };
    case 'promo':
      return {
        color: 'text-purple-500',
        iconBg: 'bg-purple-500/20',
        border: 'border-purple-500/30',
        bg: 'bg-purple-950/20'
      };
    case 'success':
    default:
      return {
        color: 'text-green-500',
        iconBg: 'bg-green-500/20',
        border: 'border-green-500/30',
        bg: 'bg-green-950/20'
      };
  }
};

export function AnnouncementBanner() {
  // Use the hook for state management
  const {
    currentAnnouncement,
    currentAnnouncements,
    currentIndex,
    loading,
    showBanner,
    handleNext,
    handlePrevious,
    handleDismiss,
    handleClose,
    hasMultipleAnnouncements
  } = useAnnouncementBanner();

  // Debug logging to help troubleshoot why announcements aren't showing
  console.log("AnnouncementBanner render attempt", { 
    loading, 
    showBanner, 
    hasAnnouncement: !!currentAnnouncement,
    announcementTitle: currentAnnouncement?.title
  });

  // Return null if there are no announcements to display or banner is closed
  if (loading || !showBanner || !currentAnnouncement) {
    return null;
  }

  const styles = getTypeStyles(currentAnnouncement.type);
  
  // Optional action URL - might exist in custom properties or as a property we add later
  const actionUrl = (currentAnnouncement as any).actionUrl;
  const hasActionUrl = Boolean(actionUrl);
  
  // Optional image URL - might exist in custom properties or as a property we add later
  const imageUrl = (currentAnnouncement as any).imageUrl;
  const hasImageUrl = Boolean(imageUrl);

  // Log what we're actually trying to render
  console.log("Rendering announcement dialog", { 
    title: currentAnnouncement.title, 
    type: currentAnnouncement.type
  });

  return (
    <Dialog open={showBanner} onOpenChange={handleClose}>
      <DialogContent className={cn(
        "sm:max-w-md max-h-[90vh] overflow-y-auto bg-black/95",
        styles.border
      )}>
        <DialogHeader className="flex justify-between items-center">
          <DialogTitle className="sr-only">
            {currentAnnouncement.title || "Announcement"}
          </DialogTitle>
          <div className={cn("flex items-center gap-2", styles.color)}>
            <Bell className="h-5 w-5" />
            <h2 className="text-xl font-bold">{currentAnnouncement.type.charAt(0).toUpperCase() + currentAnnouncement.type.slice(1)}</h2>
          </div>
        </DialogHeader>
        
        <AnimatePresence mode="wait">
          <motion.div
            key={currentAnnouncement.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center py-4"
          >
            {hasImageUrl && (
              <div className="mb-6 max-w-full overflow-hidden rounded-lg">
                <img
                  src={imageUrl}
                  alt={currentAnnouncement.title}
                  className="w-full h-auto max-h-60 object-contain"
                />
              </div>
            )}

            <h3 className="text-2xl font-bold text-center mb-2 text-white">
              {currentAnnouncement.title}
            </h3>
            
            <p className="text-center text-gray-300 mb-6 whitespace-pre-wrap">
              {currentAnnouncement.content}
            </p>

            {/* Action URL */}
            {hasActionUrl && actionUrl && (
              <Button
                size="sm"
                variant="outline"
                className={cn("mb-6", styles.color)}
                asChild
              >
                <a 
                  href={actionUrl}
                  target={typeof actionUrl === 'string' && actionUrl.startsWith("http") ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                >
                  {typeof actionUrl === 'string' && actionUrl.startsWith("http") ? (
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  ) : (
                    <Link2 className="h-3.5 w-3.5 mr-1" />
                  )}
                  View Details
                </a>
              </Button>
            )}

            {/* Navigation or Close Button */}
      
            {hasMultipleAnnouncements ? (
                <Button
                  onClick={handleNext}
                  className={cn(
                    "mt-4 w-full font-bold",
                    `bg-${currentAnnouncement.type}-600 hover:bg-${currentAnnouncement.type}-700`,
                    styles.bg
                  )}
                >
                                    Next Announcement <ChevronRight className="ml-2 h-4 w-4" />

                </Button>
            ) : null} 
            
            {/* Navigation Indicators */}
            {hasMultipleAnnouncements && (
              <div className="flex items-center justify-between w-full mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { 
                    e.stopPropagation();
                    handlePrevious();
                  }}
                  className={styles.color}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                
                <div className="flex items-center justify-center gap-1">
                  {currentAnnouncements.map((_, index) => (
                    <div
                      key={index}
                      className={`h-1.5 rounded-full transition-all ${
                        index === currentIndex
                          ? `w-6 ${styles.color}`
                          : `w-1.5 ${styles.color} opacity-30`
                      }`}
                    ></div>
                  ))}
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDismiss(false)}
                  className={styles.color}
                >
                  Don't show again
                </Button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}